import { ReactNode } from "react";
import { Skeleton, SvgIcon, Typography } from "@mui/material";
import { CoreCard } from "../core";
import i18nPackage from "../i18n/i18n";
import increaseArrow from "../images/increase-arrow.svg?react";
import decreaseArrow from "../images/decrease-arrow.svg?react";
import neutralArrow from "../images/neutral-arrow.svg?react";
import styles from "./index.module.css";

export type SectionCardProps = {
	header?: ReactNode;
	icon?: ReactNode;
	children: ReactNode;
	minWidth?: number | string;
	maxWidth?: number | string;
	scrollableDiv?: boolean | string;
	divHeight?: number | string;
};

type ChangeBadgeProps = {
	delta?: number;
};

export type StatisticCardProps = {
	header: string;
	message?: string;
	icon?: ReactNode;
	value?: number | string;
	delta?: number;
	dataLoading?: boolean;
};

export function SectionCard({
	header,
	icon,
	scrollableDiv,
	divHeight,
	children,
	minWidth,
	maxWidth,
}: SectionCardProps) {
	return (
		<div className={styles.card} style={{ minWidth: minWidth ?? 0, maxWidth: maxWidth ?? "100%" }}>
			{header && (
				<div className={styles.cardHeader}>
					{icon && <SvgIcon className={styles.cardIcon}>{icon}</SvgIcon>}
					<Typography variant="h4">{header}</Typography>
				</div>
			)}

			{scrollableDiv && divHeight ? (
				<div style={{ overflowY: "auto", height: divHeight }}>
					<div className={styles.cardContent}>{children}</div>
				</div>
			) : (
				<div className={styles.cardContent}>{children}</div>
			)}
		</div>
	);
}

function ChangeBadge({ delta }: ChangeBadgeProps) {
	if (delta === undefined) return null;

	let badgeClass = styles.neutralBadge;
	let ArrowIcon = neutralArrow;
	let sign = "+"; // The UI design has a '+' next to neutral deltas for some reason

	if (delta > 0) {
		badgeClass = styles.increaseBadge;
		ArrowIcon = increaseArrow;
		sign = "+";
	} else if (delta < 0) {
		badgeClass = styles.decreaseBadge;
		ArrowIcon = decreaseArrow;
		sign = "";
	}

	return (
		<div className={badgeClass}>
			<span>
				{sign}
				{delta.toLocaleString(i18nPackage.language)}
			</span>
			<ArrowIcon />
		</div>
	);
}

export function StatisticCard({
	header,
	message,
	icon,
	value,
	delta,
	dataLoading
}: StatisticCardProps) {
	const formattedValue = typeof value === "number" ? value.toLocaleString("en-US") : value;

	return (
		<CoreCard className={styles.statisticCard}>
			<div className={styles.statisticCardContent}>
				<div className={styles.statisticCardHeader}>
					<Typography fontWeight="500" color="#333333">
						{header}
					</Typography>
					{icon}
				</div>
				{dataLoading ? (
					<Skeleton width="150px" height="42.5px" />
				) : (
					<div className={styles.statisticCardValue}>
						<Typography fontSize="28px" fontWeight="600" color="#333333">
							{formattedValue}
						</Typography>
						<ChangeBadge delta={delta} />
					</div>
				)}
				{message && (
					<Typography color="#717171">
						{message}
					</Typography>
				)}
			</div>
		</CoreCard>
	);
}
